import { useCallback } from 'react';
import {
  OperationVariables,
  QueryHookOptions,
  TypedDocumentNode,
  useLazyQuery,
} from '@apollo/client';

import { getApiSixClientId, getLocale } from '~helpers';
import { useCacheVersion } from '~providers';

type LazyApiGatewayQueryOptions<TData, TVariables extends OperationVariables> = Omit<
  QueryHookOptions<TData, TVariables>,
  'variables'
> & {
  variables?: Omit<TVariables, 'context'>;
};

function useLazyApiGatewayQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>(query: TypedDocumentNode<TData, TVariables>) {
  const apiKey = getApiSixClientId();
  const cacheVersion = useCacheVersion();

  const [execute, result] = useLazyQuery<TData, TVariables>(query);

  const completedExecute = useCallback(
    (options?: LazyApiGatewayQueryOptions<TData, TVariables>) => {
      const { context, variables, ...restOfOptions } = options ?? {
        context: undefined,
        variables: undefined,
      };
      const { headers, ...restOfContext } = context ?? { headers: {} };
      const language = getLocale();
      return execute({
        context: {
          headers: {
            'Accept-Language': language,
            'X-CLIENT': apiKey,
            ...headers,
          },
          ...restOfContext,
        },
        variables: {
          ...variables,
          context: { cacheVersion, clientId: apiKey, language },
        } as unknown as TVariables,

        ...restOfOptions,
      });
    },
    [execute, apiKey, cacheVersion],
  );

  return [completedExecute, result] as const;
}

export default useLazyApiGatewayQuery;
